"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { PageItem, BlogPost, LeadItem, AdminUser } from "./types";

export type DbStatus = "connected" | "disconnected" | "checking";

interface AdminStoreContextType {
  // Auth state
  currentUser: AdminUser | null;
  login: (email: string, pass: string) => Promise<boolean>;
  logout: () => void;

  // DB connection status
  dbStatus: DbStatus;
  refreshFromDb: () => Promise<void>;

  // Loading state
  isLoading: boolean;
  isHydrated: boolean;

  // Pages state
  pages: PageItem[];
  addPage: (page: Omit<PageItem, "id" | "lastUpdated" | "views">) => Promise<PageItem>;
  updatePage: (id: string, updated: Partial<PageItem>) => Promise<void>;
  deletePage: (id: string) => void;
  togglePageStatus: (id: string) => void;

  // Blogs state
  blogs: BlogPost[];
  addBlog: (blog: Omit<BlogPost, "id" | "publishedDate" | "views">) => BlogPost;
  updateBlog: (id: string, updated: Partial<BlogPost>) => void;
  deleteBlog: (id: string) => void;

  // Leads state
  leads: LeadItem[];
  addLead: (lead: Omit<LeadItem, "id" | "date" | "status"> & { status?: LeadItem["status"] }) => LeadItem;
  updateLeadStatus: (id: string, status: LeadItem["status"]) => void;
  deleteLead: (id: string) => void;
}

const AdminStoreContext = createContext<AdminStoreContextType | null>(null);

const STORAGE_KEYS = {
  USER: "inno_admin_user",
  PAGES: "inno_admin_pages_v1",
  BLOGS: "inno_admin_blogs_v1",
  LEADS: "inno_admin_leads_v1",
};

export function AdminStoreProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [pages, setPages] = useState<PageItem[]>([]);
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [dbStatus, setDbStatus] = useState<DbStatus>("checking");
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Function to refresh from backend MySQL database
  const refreshFromDb = useCallback(async () => {
    try {
      setIsLoading(true);
      // 1. Check health
      const healthRes = await fetch("/api/health", { cache: "no-store" });
      const healthData = await healthRes.json().catch(() => null);

      if (healthRes.ok && healthData?.database === "connected") {
        setDbStatus("connected");

        // Concurrently fetch pages, blogs, and leads
        const [pagesRes, blogsRes, leadsRes] = await Promise.all([
          fetch("/api/pages", { cache: "no-store" }).catch(() => null),
          fetch("/api/blogs", { cache: "no-store" }).catch(() => null),
          fetch("/api/leads", { cache: "no-store" }).catch(() => null),
        ]);

        if (pagesRes && pagesRes.ok) {
          const fetchedPages = await pagesRes.json();
          if (Array.isArray(fetchedPages)) {
            setPages(fetchedPages);
            localStorage.setItem(STORAGE_KEYS.PAGES, JSON.stringify(fetchedPages));
          }
        }

        if (blogsRes && blogsRes.ok) {
          const fetchedBlogs = await blogsRes.json();
          if (Array.isArray(fetchedBlogs)) {
            setBlogs(fetchedBlogs);
            localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(fetchedBlogs));
          } else if (fetchedBlogs && Array.isArray(fetchedBlogs.blogs)) {
            setBlogs(fetchedBlogs.blogs);
            localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(fetchedBlogs.blogs));
          }
        }

        if (leadsRes && leadsRes.ok) {
          const fetchedLeads = await leadsRes.json();
          if (Array.isArray(fetchedLeads)) {
            setLeads(fetchedLeads);
            localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(fetchedLeads));
          }
        }
      } else {
        setDbStatus("disconnected");
      }
    } catch {
      setDbStatus("disconnected");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load: restore from localStorage cache if available, then sync with MySQL
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }

      const storedPages = localStorage.getItem(STORAGE_KEYS.PAGES);
      if (storedPages) {
        setPages(JSON.parse(storedPages));
      }

      const storedBlogs = localStorage.getItem(STORAGE_KEYS.BLOGS);
      if (storedBlogs) {
        setBlogs(JSON.parse(storedBlogs));
      }

      const storedLeads = localStorage.getItem(STORAGE_KEYS.LEADS);
      if (storedLeads) {
        setLeads(JSON.parse(storedLeads));
      }
    } catch {
      // Fallback
    } finally {
      setIsHydrated(true);
      refreshFromDb();
    }
  }, [refreshFromDb]);

  // Persist to localStorage when state changes after initial hydration
  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEYS.PAGES, JSON.stringify(pages));
    } catch (e) {
      console.error("Storage error:", e);
    }
  }, [pages, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(blogs));
    } catch (e) {
      console.error("Storage error:", e);
    }
  }, [blogs, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
    } catch (e) {
      console.error("Storage error:", e);
    }
  }, [leads, isHydrated]);

  // Dynamic Login via Backend API (/api/auth/login)
  const login = async (email: string, pass: string): Promise<boolean> => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password: pass }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setCurrentUser(data.user);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error("API login request failed:", err);
      return false;
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem(STORAGE_KEYS.USER);
  };

  const addPage = async (
    newPageData: Omit<PageItem, "id" | "lastUpdated" | "views">
  ): Promise<PageItem> => {
    const created: PageItem = {
      ...newPageData,
      id: `page-${Date.now()}`,
      lastUpdated: new Date().toISOString().split("T")[0],
      views: 0,
    };
    setPages((prev) => [created, ...prev]);

    try {
      const res = await fetch("/api/pages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(created),
      });
      if (res.ok) {
        const data = await res.json();
        setPages((prev) => prev.map((p) => (p.id === created.id ? data : p)));
        return data;
      }
    } catch (err) {
      console.warn("Background API sync failed:", err);
    }

    return created;
  };

  const updatePage = async (id: string, updated: Partial<PageItem>): Promise<void> => {
    setPages((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, ...updated, lastUpdated: new Date().toISOString().split("T")[0] }
          : item
      )
    );

    try {
      await fetch(`/api/pages/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      });
    } catch (err) {
      console.warn("Background API sync failed:", err);
    }
  };

  const deletePage = (id: string) => {
    setPages((prev) => prev.filter((item) => item.id !== id));

    // Send to backend API
    fetch(`/api/pages/${id}`, {
      method: "DELETE",
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  const togglePageStatus = (id: string) => {
    let nextStatus: PageItem["status"] = "Draft";
    setPages((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          nextStatus = item.status === "Live" ? "Draft" : "Live";
          return {
            ...item,
            status: nextStatus,
            lastUpdated: new Date().toISOString().split("T")[0],
          };
        }
        return item;
      })
    );

    // Send to backend API
    fetch(`/api/pages/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: nextStatus }),
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  const addBlog = (newBlogData: Omit<BlogPost, "id" | "publishedDate" | "views">): BlogPost => {
    const created: BlogPost = {
      ...newBlogData,
      id: `blog-${Date.now()}`,
      publishedDate: new Date().toISOString().split("T")[0],
      views: 0,
    };
    setBlogs((prev) => [created, ...prev]);

    // Send to backend API
    fetch("/api/blogs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(created),
    }).catch((err) => console.warn("Background API sync failed:", err));

    return created;
  };

  const updateBlog = (id: string, updated: Partial<BlogPost>) => {
    setBlogs((prev) => prev.map((b) => (b.id === id ? { ...b, ...updated } : b)));

    // Send to backend API
    fetch(`/api/blogs/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updated),
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  const deleteBlog = (id: string) => {
    setBlogs((prev) => prev.filter((b) => b.id !== id));

    // Send to backend API
    fetch(`/api/blogs/${id}`, {
      method: "DELETE",
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  const addLead = (newLeadData: Omit<LeadItem, "id" | "date" | "status"> & { status?: LeadItem["status"] }): LeadItem => {
    const created: LeadItem = {
      ...newLeadData,
      id: `lead-${Date.now()}`,
      date: new Date().toISOString().replace("T", " ").substring(0, 16),
      status: newLeadData.status || "New",
    };
    setLeads((prev) => [created, ...prev]);

    // Send to backend API
    fetch("/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(created),
    }).catch((err) => console.warn("Background API sync failed:", err));

    return created;
  };

  const updateLeadStatus = (id: string, status: LeadItem["status"]) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));

    // Send to backend API
    fetch(`/api/leads/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  const deleteLead = (id: string) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));

    // Send to backend API
    fetch(`/api/leads/${id}`, {
      method: "DELETE",
    }).catch((err) => console.warn("Background API sync failed:", err));
  };

  return (
    <AdminStoreContext.Provider
      value={{
        currentUser,
        login,
        logout,
        dbStatus,
        refreshFromDb,
        isLoading,
        isHydrated,
        pages,
        addPage,
        updatePage,
        deletePage,
        togglePageStatus,
        blogs,
        addBlog,
        updateBlog,
        deleteBlog,
        leads,
        addLead,
        updateLeadStatus,
        deleteLead,
      }}
    >
      {children}
    </AdminStoreContext.Provider>
  );
}

export function useAdminStore() {
  const context = useContext(AdminStoreContext);
  if (!context) {
    throw new Error("useAdminStore must be used within an AdminStoreProvider");
  }
  return context;
}
